import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Typography, CircularProgress, useTheme, Chip,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Checkbox
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
  LabelList
} from 'recharts';
import { planoAcaoService } from '../../services/PlanoAcaoService';
import PlanoAcaoResultadoConsulta from '../../types/negocio/PlanoAcaoResultadoConsulta';
import { calcularPercentualConclusao, formatarPercentual } from '../../utils/planoAcaoUtils';

const STATUS_COLORS: Record<string, string> = {
  'não iniciada': '#9e9e9e',
  'em andamento': '#2196f3',
  'finalizada': '#4caf50',
  'cancelada': '#f44336',
  'atrasada': '#ff9800'
};

const STATUS_LABELS: Record<string, string> = {
  'não iniciada': 'Não iniciada',
  'em andamento': 'Em andamento',
  'finalizada': 'Finalizada',
  'cancelada': 'Cancelada',
  'atrasada': 'Atrasada'
};

const normalizeStatus = (status: string): string => {
  return status.toLowerCase().trim();
};

const getStatusColor = (status: string): string => {
  return STATUS_COLORS[normalizeStatus(status)] || '#9e9e9e';
};

interface GanttDataItem {
  name: string;
  fullName: string;
  start: number;
  duration: number;
  end: number;
  status: string;
  responsavel: string;
  dataInicio: string;
  prazoConclusao: string;
  progressoFormatado: string;
  percentualConclusao: number;
}

const GanttChart: React.FC = () => {
  const theme = useTheme();
  const [data, setData] = useState<PlanoAcaoResultadoConsulta[]>([]);
  const [loading, setLoading] = useState(true);
  const [hiddenPlanGuids, setHiddenPlanGuids] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('ganttHiddenPlans');
      if (saved) {
        return new Set(JSON.parse(saved));
      }
    } catch (e) {
      console.error('Falha ao carregar planos ocultos do localStorage', e);
    }
    return new Set();
  });
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem('ganttHiddenPlans', JSON.stringify(Array.from(hiddenPlanGuids)));
    } catch (e) {
      console.error('Falha ao salvar planos ocultos no localStorage', e);
    }
  }, [hiddenPlanGuids]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const result = await planoAcaoService.buscarPlanos({ verArquivados: false });
        const filteredResult = result.filter(item => item.prazoConclusao);
        setData(filteredResult);
      } catch (error) {
        console.error('Erro ao buscar dados para Gantt:', error);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const { ganttData, minDate, maxDate, todayPosition, minDateTimestamp } = useMemo(() => {
    const visibleData = data.filter(item => {
      const itemId = item.guid || item.acao;
      return !hiddenPlanGuids.has(itemId);
    });

    if (visibleData.length === 0) {
      return { ganttData: [], minDate: 0, maxDate: 0, todayPosition: 0, minDateTimestamp: 0 };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let absoluteMinDate = Infinity;
    let absoluteMaxDate = -Infinity;

    visibleData.forEach(item => {
      const dataInicio = item.dataInicio ? new Date(item.dataInicio) : new Date();
      const prazoConclusao = new Date(item.prazoConclusao);

      dataInicio.setHours(0, 0, 0, 0);
      prazoConclusao.setHours(0, 0, 0, 0);

      absoluteMinDate = Math.min(absoluteMinDate, dataInicio.getTime());
      absoluteMaxDate = Math.max(absoluteMaxDate, prazoConclusao.getTime());
    });

    const marginDays = 7 * 24 * 60 * 60 * 1000;
    absoluteMinDate -= marginDays;
    absoluteMaxDate += marginDays;

    const totalRange = absoluteMaxDate - absoluteMinDate;

    const processedData: GanttDataItem[] = visibleData.map(item => {
      const dataInicio = item.dataInicio ? new Date(item.dataInicio) : new Date();
      const prazoConclusao = new Date(item.prazoConclusao);

      dataInicio.setHours(0, 0, 0, 0);
      prazoConclusao.setHours(0, 0, 0, 0);

      const startTime = dataInicio.getTime();
      const endTime = prazoConclusao.getTime();

      const relativeStart = startTime - absoluteMinDate;
      const duration = Math.max(endTime - startTime, 86400000);

      let status = item.status;
      const normalizedStatus = normalizeStatus(status);
      if (normalizedStatus !== 'finalizada' && normalizedStatus !== 'cancelada' && prazoConclusao < today) {
        status = 'Atrasada';
      }

      const truncatedName = item.acao.length > 100
        ? item.acao.substring(0, 100) + '...'
        : item.acao;

      const percentual = calcularPercentualConclusao(item);

      return {
        name: truncatedName,
        fullName: item.acao,
        start: relativeStart,
        duration: duration,
        end: relativeStart + duration,
        status,
        responsavel: item.responsavelNome || 'Não atribuído',
        dataInicio: dataInicio.toLocaleDateString('pt-BR'),
        prazoConclusao: prazoConclusao.toLocaleDateString('pt-BR'),
        progressoFormatado: formatarPercentual(percentual),
        percentualConclusao: percentual
      };
    });

    processedData.sort((a, b) => a.start - b.start);

    const todayPos = today.getTime() - absoluteMinDate;

    return {
      ganttData: processedData,
      minDate: 0,
      maxDate: totalRange,
      todayPosition: todayPos,
      minDateTimestamp: absoluteMinDate
    };
  }, [data, hiddenPlanGuids]);

  const togglePlanVisibility = (itemId: string) => {
    setHiddenPlanGuids(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const formatXAxis = (tickItem: number) => {
    const date = new Date(minDateTimestamp + tickItem);
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload as GanttDataItem;
      return (
        <Box
          sx={{
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            p: 1.5,
            boxShadow: theme.shadows[3],
            maxWidth: 300
          }}
        >
          <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 0.5 }}>
            {data.fullName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Responsável:</strong> {data.responsavel}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Início:</strong> {data.dataInicio}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Prazo:</strong> {data.prazoConclusao}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <strong>Progresso:</strong>
            <Box component="span" sx={{
              fontWeight: 'bold',
              color: data.percentualConclusao === 100 ? 'success.main' :
                data.percentualConclusao >= 70 ? 'info.main' :
                  data.percentualConclusao >= 30 ? 'warning.main' : 'text.primary'
            }}>
              {data.progressoFormatado}
            </Box>
          </Typography>
          <Box sx={{ mt: 0.5 }}>
            <Chip
              label={STATUS_LABELS[normalizeStatus(data.status)] || data.status}
              size="small"
              sx={{
                backgroundColor: getStatusColor(data.status),
                color: 'white',
                fontWeight: 500,
                fontSize: '0.7rem'
              }}
            />
          </Box>
        </Box>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (data.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <Typography variant="body1" color="text.secondary">
          Nenhuma ação com cronograma disponível
        </Typography>
      </Box>
    );
  }

  const chartHeight = Math.max(ganttData.length * 45 + 80, 300);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 1.5, position: 'relative' }}>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'center' }}>
          {Object.entries(STATUS_LABELS).map(([key, label]) => (
            <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: STATUS_COLORS[key] }} />
              <Typography variant="caption" color="text.secondary">
                {label}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box sx={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)' }}>
          <IconButton onClick={() => setFilterModalOpen(true)} title="Filtrar Planos de Ação">
            <FilterListIcon />
          </IconButton>
        </Box>
      </Box>

      {ganttData.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <Typography variant="body1" color="text.secondary">
            Nenhuma ação selecionada para exibição
          </Typography>
        </Box>
      ) : (
        <Box sx={{ height: chartHeight }}>
          <ResponsiveContainer width="100%" height="90%" style={{ marginTop: 2 }}>
            <BarChart
              data={ganttData}
              layout="vertical"
              margin={{ top: 25, right: 30, left: 10, bottom: 20 }}
              barCategoryGap="20%"
            >
              <XAxis
                type="number"
                domain={[minDate, maxDate]}
                tickFormatter={formatXAxis}
                tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                tickCount={8}
                axisLine={{ stroke: theme.palette.divider }}
              />
              <YAxis
                type="category"
                dataKey="name"
                tick={{ fontSize: 11, fill: theme.palette.text.primary }}
                width={280}
                axisLine={{ stroke: theme.palette.divider }}
              />
              <Tooltip content={<CustomTooltip />} />

              {/* Linha do dia atual */}
              {todayPosition >= minDate && todayPosition <= maxDate && (
                <ReferenceLine
                  x={todayPosition}
                  stroke={theme.palette.error.main}
                  strokeDasharray="5 5"
                  strokeWidth={2}
                  label={{
                    value: 'Hoje',
                    position: 'top',
                    fill: theme.palette.error.main,
                    fontSize: 11
                  }}
                />
              )}

              <Bar
                dataKey="start"
                stackId="gantt"
                fill="transparent"
                isAnimationActive={false}
              />

              <Bar
                dataKey="duration"
                stackId="gantt"
                radius={[4, 4, 4, 4]}
                isAnimationActive={true}
              >
                {ganttData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={getStatusColor(entry.status)}
                    style={{ cursor: 'pointer' }}
                  />
                ))}
                <LabelList dataKey="progressoFormatado" position="right" fill={theme.palette.text.secondary} fontSize={11} fontWeight={500} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}

      <Dialog open={filterModalOpen} onClose={() => setFilterModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Filtrar Planos de Ação</DialogTitle>
        <DialogContent dividers>
          <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
            {data.map((item) => {
              const itemId = item.guid || item.acao;
              const isChecked = !hiddenPlanGuids.has(itemId);
              return (
                <ListItem
                  key={itemId}
                  disablePadding
                >
                  <ListItemButton onClick={() => togglePlanVisibility(itemId)}>
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={isChecked}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText primary={item.acao} />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFilterModalOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GanttChart;
